/**
 * Table inputs are currently read-only. They do not have a value and are non-selectable and non-editable.
 * Grids are not reorderable, and have no hover styles over the rows. Columns are sortable and resizable.
 * When no data is returned, we still display the grid's columns, and display a custom message (overwriting the generic
 * noRecords message in DFGrid), since an empty grid can be shown here for reasons other than filtering out all data
 * (which is what our generic message states). This is to stay as consistent as possible with Flex implementation of empty
 * grids in Forms.
 */
import PromptInput from "common/widgets/PromptList/PromptInput";
import DFGrid from "app/widgets/DFGrid/DFGrid";
import locale from "common/managers/locale";
import DataType from "common/enums/DataType";
import dateConvert from "common/helper/dateConvert";
import ObjectUtil from "common/helper/ObjectUtil";
const TablePromptInput = PromptInput.extend({
    options: {
        name: "TablePromptInput",
        template: "<div></div>"
    },
    init: function(element, options) {
        PromptInput.fn.init.apply(this, arguments);
        var columns = this._getColumns();
        var opts = {
            columns: columns,
            dataSource: this._formatValues(columns, this._getValues()),
            navigatable: true,
            resizable: options.tabbable !== false,
            sortable: options.tabbable !== false ? {
                mode: "single",
                allowUnsort: false
            } : false,
            messages: {
                noRecords: locale.get("message.grid.none")
            }
        };

        this.widget = new DFGrid(this.input, opts);
        // firefox focuses the grid content div
        this.widget.content && this.widget.content.attr("tabindex", "-1");
        this.input.addClass("hideGridSelection").addClass("resizableColumns");
        // set class needed for applying styles to set width / height / labelPosition correctly,
        // based on control type
        if (this.options.model.width && this.options.model.height) {
            var container = this.element.parent();
            container.addClass("tableControl");
            container.addClass("flexLayout");
            this.element.addClass("shrinkable");
        }
        this._updateSize();

        this.refresh();
    },

    /**
     * Get the columns from the table data
     * @returns {*}
     * @private
     */
    _getColumns: function() {
        var columns = this._getKeyValue("columnData");
        if (columns.length === 0) {
            return [];
        }
        return columns.map(function(column, index) {
            return {
                title: column.key || locale.get("column.name.empty"),
                field: "value[" + index + "].value",
                type: column.value
            }
        });
    },

    /**
     * Get the values of the table data
     * @returns {*}
     * @private
     */
    _getValues: function() {
        return this._getKeyValue("tabularData");
    },

    /**
     * Formats the values of the table data based on column data type
     * @param columns {Array} Column data
     * @param values {Array} Table row data
     * @returns {*}
     * @private
     */
    _formatValues: function(columns, values) {
        if (!ObjectUtil.isArray(values) || !columns.length || !values.length) {
            return [];
        }
        values.forEach(function(row) {
            row.value.forEach(function(cell, index) {
                if (columns[index].type === DataType.DATE) {
                    cell.value = locale.formatDate(dateConvert.toDate(cell.value));
                }
            });
        });
        return values;
    },

    /**
     * Gets the value of the key from the table data
     * @param key {string} Key of the data
     * @returns {*}
     * @private
     */
    _getKeyValue: function(key) {
        if (!this.options.model.data) {
            return [];
        }
        var values = this.options.model.data.filter(function(data) {
            return data.key === key;
        });
        if (values.length === 0) {
            return [];
        }
        return values[0].value;
    },

    // Do nothing - non-input type prompt has no value
    handleChange: $.noop,

    // Do nothing - non-input type prompt has no value
    updateWidgetValue: $.noop,

    // Do nothing - non-input type prompt has no value
    value: $.noop,

    getMinSize: function() {
        var minSize = PromptInput.fn.getMinSize.apply(this, arguments);
        minSize.width = 50;
        minSize.height = 50;
        return minSize;
    },

    updateSize: function() {
        PromptInput.fn.updateSize.apply(this, arguments);
        this._updateSize();
    },

    _updateSize: function() {
        if (this.options.width && this.options.height) {
            this.element.parent().css("height", this.options.height + "px");
        }
    },

    /**
     * Override to prevent tabindex set on input since dfgrid has an atypical tabindex handler in readonly method
     * @private
     */
    disableTabbable: function() {
        this.widget.table.attr("tabindex", "-1");
    },

    /**
     * Override
     */
    canControlAcceptFocusWithNoData() {
        return false;
    },

    /**
     * Override
     */
    hasData() {
        return this.getData()?.find((part) => part.key === "tabularData")?.value.length > 0;
    }
});
kendo.ui.plugin(TablePromptInput);
export default TablePromptInput;
